import { z } from 'zod';

// Common validation schemas
export const emailSchema = z
  .string()
  .trim()
  .email({ message: "Email inválido" })
  .max(255, { message: "Email deve ter no máximo 255 caracteres" });

export const phoneSchema = z
  .string()
  .trim()
  .min(10, { message: "Telefone deve ter pelo menos 10 dígitos" })
  .max(15, { message: "Telefone deve ter no máximo 15 dígitos" })
  .regex(/^[\d\s()+-]+$/, { message: "Formato de telefone inválido" });

export const nameSchema = z
  .string()
  .trim()
  .min(2, { message: "Nome deve ter pelo menos 2 caracteres" })
  .max(100, { message: "Nome deve ter no máximo 100 caracteres" })
  .regex(/^[a-zA-ZÀ-ÿ\s]+$/, { message: "Nome deve conter apenas letras e espaços" });

export const messageSchema = z
  .string()
  .trim()
  .min(10, { message: "Mensagem deve ter pelo menos 10 caracteres" })
  .max(1000, { message: "Mensagem deve ter no máximo 1000 caracteres" });

// Lead form validation schema
export const leadFormSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema,
  message: messageSchema.optional(),
});

// Blog content validation schema
export const blogContentSchema = z.object({
  title: z
    .string()
    .trim()
    .min(5, { message: "Título deve ter pelo menos 5 caracteres" })
    .max(200, { message: "Título deve ter no máximo 200 caracteres" }),
  content: z
    .string()
    .trim()
    .min(50, { message: "Conteúdo deve ter pelo menos 50 caracteres" })
    .max(50000, { message: "Conteúdo deve ter no máximo 50000 caracteres" }),
  excerpt: z
    .string()
    .trim()
    .max(300, { message: "Resumo deve ter no máximo 300 caracteres" })
    .optional(),
});

// FAQ validation schema
export const faqSchema = z.object({
  question: z
    .string()
    .trim()
    .min(5, { message: "Pergunta deve ter pelo menos 5 caracteres" })
    .max(500, { message: "Pergunta deve ter no máximo 500 caracteres" }),
  answer: z
    .string()
    .trim()
    .min(10, { message: "Resposta deve ter pelo menos 10 caracteres" })
    .max(2000, { message: "Resposta deve ter no máximo 2000 caracteres" }),
  category: z
    .string()
    .trim()
    .min(2, { message: "Categoria deve ter pelo menos 2 caracteres" })
    .max(50, { message: "Categoria deve ter no máximo 50 caracteres" }),
});

// Rate limiting helper
export const createRateLimiter = (maxAttempts: number, windowMs: number) => {
  const attempts = new Map<string, { count: number; resetTime: number }>();

  return {
    isAllowed: (identifier: string): boolean => {
      const now = Date.now();
      const userAttempts = attempts.get(identifier);

      if (!userAttempts || now > userAttempts.resetTime) {
        attempts.set(identifier, { count: 1, resetTime: now + windowMs });
        return true;
      }

      if (userAttempts.count >= maxAttempts) {
        return false;
      }

      userAttempts.count++;
      return true;
    },
    getRemainingTime: (identifier: string): number => {
      const userAttempts = attempts.get(identifier);
      if (!userAttempts) return 0;
      return Math.max(0, userAttempts.resetTime - Date.now());
    }
  };
};